import axios from "axios";
import toast from "react-hot-toast";
import { AuthService } from "@/services/authService";
// DISABLED-FEATURE: browser extension is switched off — see server/app/api/v2/api.py.
// import { ExtensionService } from "@/services/extensionService";
import { getTenantSlug } from "@/utils/tenantSlug";
import { isInsufficientPermissionsError } from "@/utils/apiErrorUtils";
import { getTenantScope } from "@/lib/tenantScope";
import { forceFullLogout } from "@/lib/forceLogout";
import { noteResponseConfigVersion } from "@/lib/configVersion";
import { getAccessToken, needsRenewal } from "@/lib/tokenSession";
import { MODULE_UNAVAILABLE_MESSAGE } from "@/permissions/modules";
import { messageForError } from "@/lib/errorMessages";

/**
 * Base URL for API requests
 * Uses environment variable or falls back to localhost for development
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";
const DEFAULT_API_TIMEOUT_MS = 30_000;
// VITE_API_TIMEOUT_MS (or the legacy VITE_API_TIMEOUT) is expressed in milliseconds.
const configuredApiTimeoutMs = Number(import.meta.env.VITE_API_TIMEOUT_MS ?? import.meta.env.VITE_API_TIMEOUT);
const API_TIMEOUT_MS = Number.isFinite(configuredApiTimeoutMs) && configuredApiTimeoutMs > 0 ? configuredApiTimeoutMs : DEFAULT_API_TIMEOUT_MS;
// WebSocket
const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
export const WS_URL = import.meta.env.VITE_WS_URL || `${protocol}//${window.location.host}/ws/events`;

const logoutUser = () => {
  forceFullLogout();
};

/**
 * Configured Axios instance for making API requests
 * Sets default headers, timeout and base URL
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: API_TIMEOUT_MS,
  withCredentials: true,
});

/**
 * Request interceptor for all API calls
 *
 * Attaches the token, and renews it *first* when it is about to expire. That
 * pre-flight renewal is what makes the common case quiet: without it every
 * token expiry costs the user a failed request, a refresh and a retry, and any
 * request that cannot be replayed safely simply loses.
 *
 * `skipAuthRefresh` opts a call out of both the pre-flight renewal and the
 * 401 retry. `/auth/refresh` and `/auth/logout` set it — they carry the
 * expiring token on purpose, and renewing before renewing would recurse.
 */
apiClient.interceptors.request.use(
  async (config) => {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      return Promise.reject(new Error("NETWORK_OFFLINE"));
    }

    if (config.secure && !config.skipAuthRefresh && needsRenewal()) {
      try {
        await AuthService.refresh();
      } catch {
        // Fall through with whatever token we have. If it really is dead the
        // response interceptor below handles the 401 — deciding to sign the
        // user out from inside a request interceptor would do it on a blip.
      }
    }

    if (config.secure) {
      const accessToken = getAccessToken();
      if (accessToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }

    config.headers["X-Tenant-Slug"] = getTenantSlug();

    // The tenant a SuperAdmin is working inside. Ignored by the API for
    // everyone else, and never set for them. A call that already names its
    // tenant (`?tenantId=`) wins server-side, so this is only a default.
    //
    // `crossTenant: true` opts out: a few SuperAdmin views mean the whole
    // platform, not one tenant (the dashboard's recent-users panel). They say
    // so at the call site rather than inheriting an invisible scope.
    const tenantScope = getTenantScope();
    if (config.secure && tenantScope && !config.crossTenant) {
      config.headers["X-Target-Tenant-Id"] = tenantScope;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Response interceptor for all API calls
 * Handles authentication errors and token expiration
 */
apiClient.interceptors.response.use(
  (response) => {
    // Reconciliation: every response carries X-Config-Version. If it is ahead
    // of what Redux holds, our filtered permissions and unavailable-modules
    // list are stale and we refetch /auth/current-user (debounced). This is
    // what makes configuration changes reliable when the WebSocket push is
    // missed — see lib/configVersion.js for why it cannot be the only signal.
    noteResponseConfigVersion(response);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Errors carry the header too, and a 403 MODULE_NOT_ENABLED is exactly the
    // moment the client most needs to notice it is behind.
    if (error.response) {
      noteResponseConfigVersion(error.response);
    }

    // The sentence to show a user, chosen here from the `error_code` rather
    // than forwarded from the server (`lib/errorMessages.js`, I1768 F10).
    // Attached to every rejection, so a caller shows client copy by reading
    // one property instead of reaching into `response.data.message` — which
    // is what put the server's own wording on 230 error paths.
    error.userMessage = messageForError(error);

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // New deployment detected — the access token's deployment_id no longer matches
    // the server. Refreshing would also be rejected, so skip it and hard-logout
    // (wipes all client storage and redirects). Covers backend-only deploys where
    // the frontend bundle is unchanged so deploymentGuard never fires.
    if (error.response?.data?.error_code === "DEPLOYMENT_RESTART") {
      forceFullLogout();
      return Promise.reject(error);
    }

    // Tenant deactivated mid-session (or attempt to log into a deactivated
    // tenant). Backend returns 403 + COMPANY_SUSPENDED with the friendly
    // message in `data.message`. We pass a `reason` query param to the
    // login page so it can render that message as a toast — query params
    // survive the hard navigation reliably (no sessionStorage games).
    // Narrow check: only triggers when BOTH conditions match, so every
    // other 403 in the app keeps its current behaviour.
    if (error.response?.status === 403 && error.response?.data?.error_code === "COMPANY_SUSPENDED") {
      forceFullLogout({ reason: "org_suspended" });
      return Promise.reject(error);
    }
    
    // This account was deactivated mid-session. Same shape as the tenant case
    // above, one level down: nothing this browser holds is usable any more, and
    // the session renewal timer would otherwise keep asking every few minutes
    // while the app carried on rendering as if the user were still signed in.
    if (error.response?.status === 403 && error.response?.data?.error_code === "USER_DEACTIVATED") {
      forceFullLogout({ reason: "user_deactivated" });
      return Promise.reject(error);
    }

    if (isInsufficientPermissionsError(error)) {
      if (!originalRequest.skipPermissionToast) {
        toast.error(error.userMessage, { id: "insufficient-permissions" });
      }
      return Promise.reject(error);
    }

    // The tenant is not entitled to this module — it is not in their plan.
    //
    // **No toast.** A tenant should never see this response at all: the
    // client knows which surface belongs to which module, and a surface they
    // do not own is not rendered, so nothing calls the endpoint. A 403
    // arriving here therefore means a surface was missed, and telling the
    // tenant "this feature is not enabled" about something they never bought
    // and never clicked explains nothing.
    //
    // It is a bug for us, so it is logged for us, in development only. Fix
    // the surface — see `permissions/moduleSurfaces.js`. The rejection still
    // goes through, so the caller sees the error as before.
    if (error.response?.status === 403 && error.response?.data?.error_code === "MODULE_NOT_ENABLED") {
      if (import.meta.env.DEV) {
        const moduleCode = error.response.data?.data?.module || "unknown";
        console.warn(
          `[modules] 403 MODULE_NOT_ENABLED for "${moduleCode}" on ${originalRequest?.url}. ` +
            "A surface for this module is ungated — the client should never have made this call.",
        );
      }
      return Promise.reject(error);
    }

    // Entitled, but the module is switched off fleet-wide right now. 503, not
    // 403: nothing is wrong with this user's access. Same rule — surface it,
    // never navigate. The page keeps rendering whatever the tenant already
    // generated; only the action control is disabled.
    if (error.response?.status === 503 && error.response?.data?.error_code === "MODULE_TEMPORARILY_UNAVAILABLE") {
      const moduleCode = error.response.data?.data?.module || "unknown";
      toast.error(messageForError(error, MODULE_UNAVAILABLE_MESSAGE), {
        id: `module-unavailable:${moduleCode}`,
      });
      return Promise.reject(error);
    }

    // The renewal itself was refused. The refresh cookie is gone, revoked or
    // expired — nothing left to recover from, so sign out for real. Sending a
    // 401 body through unchanged would leave the app running against a session
    // the server has already ended.
    if (originalRequest.skipAuthRefresh && error.response?.status === 401) {
      if (originalRequest.url?.includes("/auth/refresh")) {
        logoutUser();
      }
      return Promise.reject(error);
    }

    // A secure call came back 401. Renew once and replay it.
    //
    // No queue and no `isRefreshing` flag here any more: `AuthService.refresh`
    // is single-flight, so ten simultaneous 401s await one shared request and
    // then each replay themselves. The queue this replaces had a failure mode
    // of its own — a rejected subscriber could resolve after the flag was
    // already cleared, and every queued call died with TOKEN_REFRESH_FAILED.
    if (error.response?.status === 401 && originalRequest.secure && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshed = await AuthService.refresh();
        const newAccessToken = refreshed?.accessToken || refreshed?.access_token || "";

        if (!newAccessToken) {
          throw new Error("NO_NEW_ACCESS_TOKEN");
        }

        if (typeof window !== "undefined") {
          sessionStorage.setItem("accessToken", newAccessToken);
        }

        // Update default header
        apiClient.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

        // DISABLED-FEATURE: browser extension is switched off — see server/app/api/v2/api.py.
        // // Fire-and-forget: keep the browser extension's long-lived token
        // // fresh on every silent refresh, not only at login. Without this,
        // // the extension token only ever gets renewed when the user
        // // explicitly logs in again — this is what makes it self-heal on
        // // its own instead. No-ops silently when the extension isn't
        // // installed; never allowed to block the actual token refresh.
        // ExtensionService.waitForExtension(800)
        //   .then((meta) => {
        //     if (!meta) {
        //       console.warn("[TalentFlow ext] refresh push skipped — bridge.js not detected on this page.");
        //       return null;
        //     }
        //     return ExtensionService.pushJwt(newAccessToken).then((res) => {
        //       if (!res || !res.ok) console.warn("[TalentFlow ext] refresh JWT push failed:", res);
        //     });
        //   })
        //   .catch((err) => console.warn("[TalentFlow ext] refresh push threw:", err));

        // Retry original request
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        logoutUser();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

/**
 * API service wrapper functions
 * Provides typed methods for REST operations with consistent error handling
 */
export const apiService = {
  /**
   * Makes a GET request to the specified endpoint
   * @param url - The API endpoint to call
   * @param config - Optional Axios request configuration
   * @returns Promise with the API response
   */
  get: (url, config) => apiClient.get(url, config),

  /**
   * Makes a GET request to the specified endpoint with Token
   * @param url - The API endpoint to call
   * @param config - Optional Axios request configuration
   * @returns Promise with the API response
   */
  getSecure: (url, config) => {
    return apiClient.get(url, { ...config, secure: true });
  },

  /**
   * Makes a POST request to the specified endpoint
   * @param url - The API endpoint to call
   * @param data - The data to send in the request body
   * @param config - Optional Axios request configuration
   * @returns Promise with the API response
   */
  post: (url, data, config) => apiClient.post(url, data, config),

  /**
   * Makes a POST request to the specified endpoint
   * @param url - The API endpoint to call
   * @param data - The data to send in the request body
   * @param config - Optional Axios request configuration
   * @returns Promise with the API response
   */
  postSecure: (url, data, config) => {
    return apiClient.post(url, data, { ...config, secure: true });
  },

  /**
   * Makes a PUT request to the specified endpoint
   * @param url - The API endpoint to call
   * @param data - The data to send in the request body
   * @param config - Optional Axios request configuration
   * @returns Promise with the API response
   */
  put: (url, data, config) => apiClient.put(url, data, config),

  /**
   * Makes a PUT request to the specified endpoint with Token
   * @param url - The API endpoint to call
   * @param data - The data to send in the request body
   * @param config - Optional Axios request configuration
   * @returns Promise with the API response
   */
  putSecure: (url, data, config) => {
    return apiClient.put(url, data, { ...config, secure: true });
  },

  /**
   * Makes a DELETE request to the specified endpoint
   * @param url - The API endpoint to call
   * @param config - Optional Axios request configuration
   * @returns Promise with the API response
   */
  delete: (url, config) => {
    return apiClient.delete(url, { ...config, secure: true });
  },

  /**
   * Makes a PATCH request to the specified endpoint
   * @param url - The API endpoint to call
   * @param data - The data to send in the request body
   * @param config - Optional Axios request configuration
   * @returns Promise with the API response
   */
  patch: (url, data, config) => {
    return apiClient.patch(url, data, { ...config, secure: true });
  },
};

export default apiService;
